import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { getGuestSession, setGuestSession, clearGuestSession } from "@/lib/guest";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);     // admin user object | false | null(loading)
  const [guest, setGuest] = useState(null);   // {name, role} | false | null(loading)
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    // 1) Admin token validation
    const token = localStorage.getItem("wedding_admin_token");
    if (token) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem("wedding_admin_token");
        setUser(false);
      }
    } else {
      setUser(false);
    }

    // 2) Guest session
    const session = getGuestSession();
    setGuest(session || false);

    setChecked(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Couple/admin login (used by /admin/login page) — name optional
  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username: username || "novios", password });
    localStorage.setItem("wedding_admin_token", data.token);
    setUser({ username: data.username, role: "admin" });
    // Also start a guest session as admin so the gate lets them through
    const s = { name: data.username || "Novios", role: "admin" };
    setGuestSession(s);
    setGuest(s);
    return data;
  };

  // Unified gate login (welcome screen): name + code → guest or admin
  const gateLogin = async (name, code) => {
    const { data } = await api.post("/auth/guest-login", { name, code });
    if (data.role === "admin" && data.token) {
      localStorage.setItem("wedding_admin_token", data.token);
      setUser({ username: "novios", role: "admin" });
    }
    const s = { name: data.name, role: data.role };
    setGuestSession(s);
    setGuest(s);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("wedding_admin_token");
    clearGuestSession();
    setUser(false);
    setGuest(false);
  };

  return (
    <AuthCtx.Provider value={{ user, guest, checked, login, gateLogin, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
